import { useState, useMemo, useCallback } from 'react';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import { Link, useLocation } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import {
  usePaymentAccountsPaginated,
  useDeletePaymentAccount,
} from '@/lib/hooks/usePaymentAccounts';
import { usePermission } from '@/lib/rbac/usePermission';
import { Button } from '@/components/ui/button';
import { EditActionLink, ViewActionLink } from '@/components/common/AdminTableRowActions';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/custom/forms/SearchableSelect';
import { SearchFilter, StatusFilter } from '@/components/custom/filters';
import type { PaymentAccount } from '@cms/models';
import { STATUS_LOWER, STATUS_OPTIONS } from '@cms/utils';
import { withAreaPrefix } from '@/lib/area-routes';

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
type StatusFilterValue = (typeof STATUS_OPTIONS)[number]['value'] | '';
type TypeFilterValue = 'BANK_TRANSFER' | 'CRYPTO' | '';

const thClass =
  'px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider';
const tdClass = 'px-6 py-4 whitespace-nowrap text-sm';

function getSortIndicator(sorted: false | 'asc' | 'desc'): string {
  if (sorted === 'asc') return ' ↑';
  if (sorted === 'desc') return ' ↓';
  return ' ⇅';
}

function getDetailsText(account: PaymentAccount): string {
  if (account.type === 'BANK_TRANSFER') {
    return [account.bankName, account.accountNumber].filter(Boolean).join(' / ') || '—';
  }
  return [account.network, account.walletAddress].filter(Boolean).join(' / ') || '—';
}

export function PaymentAccountsPage() {
  const { col, act, page, statusOptions, allStatusesOption, tableLoading } = useAdminI18n();
  const location = useLocation();
  const toAreaPath = (path: string): string => withAreaPrefix(path, location.pathname);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'sortOrder', desc: false }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('');
  const [typeFilter, setTypeFilter] = useState<TypeFilterValue>('');
  const typeOptions: SearchableSelectOption[] = useMemo(
    () => [
      { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
      { value: 'CRYPTO', label: 'Crypto' },
    ],
    []
  );
  const canView = usePermission('payment-account:read');
  const canUpdate = usePermission('payment-account:update');
  const canDelete = usePermission('payment-account:delete');
  const canCreate = usePermission('payment-account:create');

  const currentPage = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize as (typeof PAGE_SIZE_OPTIONS)[number];

  const sortBy = ['name', 'type', 'status', 'sortOrder'].includes(sorting[0]?.id ?? '')
    ? (sorting[0]?.id as 'name' | 'type' | 'status' | 'sortOrder')
    : 'sortOrder';
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';

  const { data: listResponse, isLoading } = usePaymentAccountsPaginated({
    page: currentPage,
    pageSize,
    search: globalFilter || undefined,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
    sortBy,
    sortOrder,
  });
  const accounts = listResponse?.data ?? [];
  const totalRecords = listResponse?.total ?? 0;
  const deleteMutation = useDeletePaymentAccount();

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const handleDelete = useCallback(
    async (id: string, name: string) => {
      if (confirm(`Are you sure you want to delete payment account "${name}"?`)) {
        try {
          await deleteMutation.mutateAsync(id);
        } catch (error: unknown) {
          const err = error as { response?: { data?: { message?: string } } };
          alert(err.response?.data?.message || 'Failed to delete payment account');
        }
      }
    },
    [deleteMutation]
  );

  const columns = useMemo<ColumnDef<PaymentAccount>[]>(
    () => [
      {
        id: 'no',
        header: col('no'),
        cell: ({ row }) => (safePage - 1) * pageSize + row.index + 1,
        size: 56,
        meta: { className: 'text-muted-foreground w-14' },
      },
      {
        accessorKey: 'type',
        header: col('type'),
        enableSorting: true,
        cell: ({ getValue }) => (
          <span className="font-medium text-foreground">
            {(getValue() as string) === 'BANK_TRANSFER' ? 'Bank Transfer' : 'Crypto'}
          </span>
        ),
        meta: { className: '' },
      },
      {
        accessorKey: 'name',
        header: col('name'),
        enableSorting: true,
        cell: ({ getValue }) => (
          <span className="font-medium text-foreground">{getValue() as string}</span>
        ),
        meta: { className: '' },
      },
      {
        id: 'details',
        header: col('details'),
        cell: ({ row }) => (
          <span
            className="text-muted-foreground max-w-xs truncate block"
            title={getDetailsText(row.original)}
          >
            {getDetailsText(row.original)}
          </span>
        ),
        meta: { className: 'max-w-[200px]' },
      },
      {
        accessorKey: 'currency',
        header: col('currency'),
        cell: ({ getValue }) => (
          <span className="text-muted-foreground">{(getValue() as string | null) ?? '—'}</span>
        ),
        meta: { className: '' },
      },
      {
        accessorKey: 'status',
        header: col('status'),
        enableSorting: true,
        cell: ({ getValue }) => {
          const status = getValue() as string;
          return (
            <span
              className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full ${
                status?.toLowerCase() === STATUS_LOWER.ACTIVE
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-400'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : ''}
            </span>
          );
        },
        meta: { className: '' },
      },
      {
        id: 'actions',
        header: col('actions'),
        size: 260,
        minSize: 220,
        meta: { className: 'min-w-[220px]' },
        cell: ({ row }) => (
          <div className="flex items-center gap-2 flex-wrap">
            {canView && (
              <ViewActionLink to={toAreaPath(`/settings/payment-accounts/${row.original.id}`)}>
                View
              </ViewActionLink>
            )}
            {canUpdate && (
              <EditActionLink to={toAreaPath(`/settings/payment-accounts/${row.original.id}`)}>
                Edit
              </EditActionLink>
            )}
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                className="h-9 px-3 border-red-500/50 text-red-600 hover:bg-red-500/10 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/20"
                onClick={() => handleDelete(row.original.id, row.original.name)}
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [act, col, safePage, pageSize, canView, canUpdate, canDelete, handleDelete, toAreaPath]
  );

  const table = useReactTable({
    data: accounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    rowCount: totalRecords,
    state: {
      pagination: {
        pageIndex: safePage - 1,
        pageSize,
      },
      sorting,
      globalFilter,
    },
    manualSorting: true,
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(next);
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    },
    onPaginationChange: updater => {
      const current = { pageIndex: safePage - 1, pageSize };
      const next = typeof updater === 'function' ? updater(current) : updater;
      setPagination({
        pageIndex: next.pageIndex ?? 0,
        pageSize: next.pageSize ?? pageSize,
      });
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, pageIndex: 0 }));
  };

  const handleSearchChange = (value: string) => {
    setGlobalFilter(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleStatusFilterChange = (value: StatusFilterValue) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleTypeFilterChange = (value: TypeFilterValue) => {
    setTypeFilter(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{page('payment_accounts', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('payment_accounts', 'subtitle')}</p>
        </div>
        {canCreate && (
          <Link
            to={toAreaPath('/settings/payment-accounts/new')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium"
          >
            {page('payment_accounts', 'new')}
          </Link>
        )}
      </div>

      <div className="glass-card overflow-hidden">
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <SearchFilter
              id="payment-accounts-search"
              label="Search"
              value={globalFilter}
              onChange={v => handleSearchChange(v)}
              placeholder="Search (name, bank, account, wallet)…"
            />
            <SearchableSelect
              options={typeOptions}
              value={typeFilter}
              onChange={v => handleTypeFilterChange(v as TypeFilterValue)}
              placeholder="Search type..."
              emptyOption={{ value: '', label: 'All types' }}
              className="min-w-[140px]"
            />
            <StatusFilter
              id="payment-accounts-status"
              label="Status"
              value={statusFilter}
              onChange={v => handleStatusFilterChange(v as StatusFilterValue)}
              options={statusOptions}
              emptyOption={allStatusesOption}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">
              Total: <strong className="text-foreground">{totalRecords}</strong> record
              {totalRecords === 1 ? '' : 's'}
            </span>
            <label className="flex items-center gap-2 text-sm text-muted-foreground ml-auto">
              Page size:{' '}
              <select
                value={pageSize}
                onChange={e => handlePageSizeChange(Number(e.target.value))}
                className="h-9 px-3 rounded-md border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/20 focus:outline-none"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className={`${thClass} ${(header.column.columnDef.meta as { className?: string } | undefined)?.className ?? ''} ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-muted/80' : ''}`}
                      style={{ width: header.getSize() }}
                      onClick={
                        header.column.getCanSort()
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="text-muted-foreground">
                            {getSortIndicator(header.column.getIsSorted())}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="h-24 text-center">
                    <div className="flex justify-center items-center gap-2 text-muted-foreground">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="animate-spin"
                      >
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                      {tableLoading}
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-muted/50 transition-colors">
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className={`${tdClass} ${(cell.column.columnDef.meta as { className?: string } | undefined)?.className ?? ''}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && (
          <div className="p-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">
              Showing {totalRecords === 0 ? 0 : (safePage - 1) * pageSize + 1}–
              {Math.min(safePage * pageSize, totalRecords)} of {totalRecords}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() =>
                  setPagination(p => ({
                    ...p,
                    pageIndex: Math.max(0, p.pageIndex - 1),
                  }))
                }
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                Page {safePage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() =>
                  setPagination(p => ({
                    ...p,
                    pageIndex: Math.min(totalPages - 1, p.pageIndex + 1),
                  }))
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
